import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Loader2, Check, Clock, AlertTriangle, ShieldCheck, Landmark, ArrowRight,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import type { UseDepositOnboardingResult } from '@/hooks/useDepositOnboarding';
import {
  DEPOSIT_BANKS,
  getDepositBank,
  type DepositBankCode,
} from '@/supabase/deposit/banks';
import { StatusScreen } from './OnboardingStatusScreen';
import { OnboardingVerifyScreens } from './OnboardingVerifyScreens';
import { ShinhanRegistrationGuide } from './ShinhanRegistrationGuide';
import { HanaRegistrationGuide } from './HanaRegistrationGuide';
import { NonghyupRegistrationGuide } from './NonghyupRegistrationGuide';
import { WooriRegistrationGuide } from './WooriRegistrationGuide';
import { IBKRegistrationGuide } from './IBKRegistrationGuide';
import { KBRegistrationGuide } from './KBRegistrationGuide';
import { GATEWAY_NUMBER_DISPLAY } from './wooriGuide';

type Props = Pick<
  UseDepositOnboardingResult,
  'phase' | 'session' | 'authCode' | 'codeRemainingSec' | 'registered' | 'busyUntil' | 'errorMessage'
  | 'verifyRemainingSec' | 'start' | 'complete' | 'cancel' | 'reset'
> & { prefillAccount?: string; onClose: () => void };

export function OnboardingWizard(props: Props) {
  const {
    phase, session, authCode, codeRemainingSec, registered, busyUntil, errorMessage,
    verifyRemainingSec, start, complete, cancel, reset, prefillAccount, onClose,
  } = props;

  const [accountNumber, setAccountNumber] = useState(prefillAccount ?? '');
  const [selectedBank, setSelectedBank] = useState<DepositBankCode | ''>('');
  const [bankError, setBankError] = useState<string | null>(null);
  const [accountError, setAccountError] = useState<string | null>(null);

  // 모달이 새로 열려 prefill이 바뀌면 입력 동기화.
  useEffect(() => { setAccountNumber(prefillAccount ?? ''); }, [prefillAccount]);

  const submitAccount = () => {
    const digits = accountNumber.replace(/\D/g, '');
    if (!selectedBank) { setBankError('은행을 선택해주세요.'); return; }
    if (!digits) { setAccountError('계좌번호를 입력해주세요.'); return; }
    setBankError(null);
    setAccountError(null);
    start(selectedBank, digits);
  };

  const selectedBankLabel = selectedBank
    ? getDepositBank(selectedBank)?.label
    : null;

  // ---- 계좌 입력 ----
  if (phase === 'input' || phase === 'starting') {
    const busy = phase === 'starting';
    return (
      <div className="space-y-5">
        <div className="flex items-center gap-3 rounded-xl bg-slate-50 px-4 py-3.5 ring-1 ring-slate-100">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white text-teal-600 ring-1 ring-slate-200">
            <Landmark className="h-5 w-5" />
          </div>
          <p className="text-sm leading-relaxed text-slate-600">
            계좌를 연동하면, <span className="font-medium text-slate-900">입금이 자동으로 주문과 매칭돼요.</span>
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="onb-bank" className="text-sm font-medium text-slate-700">
            은행
          </Label>
          <select
            id="onb-bank"
            value={selectedBank}
            onChange={(event) => {
              setSelectedBank(event.target.value as DepositBankCode);
              setBankError(null);
            }}
            disabled={busy}
            className={cn(
              'flex h-11 w-full rounded-xl border bg-white px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-500 disabled:cursor-not-allowed disabled:opacity-60',
              bankError && 'border-rose-300 focus:ring-rose-400/30 focus:border-rose-400',
              !selectedBank && 'text-slate-400',
            )}
          >
            <option value="" disabled>은행 선택</option>
            {DEPOSIT_BANKS.map((bank) => (
              <option
                key={bank.code}
                value={bank.code}
                disabled={!bank.onboardingAvailable}
                className="text-slate-900"
              >
                {bank.label}{bank.onboardingAvailable ? '' : ' (준비 중)'}
              </option>
            ))}
          </select>
          {bankError && <p className="text-xs text-rose-500">{bankError}</p>}
        </div>

        <div className="space-y-2">
          <Label htmlFor="onb-account" className="text-sm font-medium text-slate-700">
            {selectedBankLabel ? `${selectedBankLabel} 계좌번호` : '계좌번호'}
          </Label>
          <Input
            id="onb-account"
            value={accountNumber}
            onChange={(e) => { setAccountNumber(e.target.value.replace(/\D/g, '')); if (accountError) setAccountError(null); }}
            placeholder="숫자만 입력 (- 없이)"
            inputMode="numeric"
            disabled={busy}
            className={cn(
              'h-11 rounded-xl border-slate-200 font-mono tabular-nums text-slate-900 transition-shadow placeholder:font-sans placeholder:text-slate-400 focus-visible:ring-2 focus-visible:ring-teal-500/30 focus-visible:border-teal-500',
              accountError && 'border-rose-300 focus-visible:ring-rose-400/30 focus-visible:border-rose-400',
            )}
          />
          {accountError
            ? <p className="text-xs text-rose-500">{accountError}</p>
            : <p className="text-xs text-slate-400">셀러헬퍼는 계좌 비밀번호·OTP를 절대 요구하지 않아요.</p>}
        </div>

        <Button
          className="h-11 w-full rounded-xl bg-teal-600 text-[15px] font-semibold text-white shadow-sm transition-colors hover:bg-teal-700 disabled:opacity-60"
          onClick={submitAccount}
          disabled={busy}
        >
          {busy
            ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> 연동 시작 중…</>
            : <>연동 시작 <ArrowRight className="ml-1.5 h-4 w-4" /></>}
        </Button>
      </div>
    );
  }

  // ---- 등록 안내 + 인증코드(대기/도착) ----
  if (phase === 'awaiting_code' || phase === 'code_received') {
    const guideProps = {
      authCode,
      codeRemainingSec,
      registered,
      onCancel: cancel,
      onComplete: complete,
    };

    if (session?.bank_code === 'Shinhan') {
      return <ShinhanRegistrationGuide {...guideProps} />;
    }
    if (session?.bank_code === 'Nonghyup') {
      return <NonghyupRegistrationGuide {...guideProps} />;
    }
    if (session?.bank_code === 'IBK') {
      return <IBKRegistrationGuide {...guideProps} />;
    }
    if (session?.bank_code === 'Hana') {
      return (
        <HanaRegistrationGuide
          registered={registered}
          onCancel={cancel}
          onComplete={complete}
        />
      );
    }
    if (session?.bank_code === 'KB') {
      return (
        <KBRegistrationGuide
          registered={registered}
          onCancel={cancel}
          onComplete={complete}
        />
      );
    }

    return (
      <WooriRegistrationGuide {...guideProps} />
    );
  }

  // 컨테이너가 토스트를 표시하고 즉시 닫는 전이 상태. 별도 만료 화면은 노출하지 않는다.
  if (phase === 'session_expired') return null;

  // ---- 완료 중 ----
  if (phase === 'completing') {
    return (
      <div className="flex flex-col items-center gap-3 py-12 text-slate-500">
        <Loader2 className="h-7 w-7 animate-spin text-teal-500" />
        <p className="text-sm">연동을 마무리하는 중…</p>
      </div>
    );
  }

  // ---- 1원 검증(verifying/confirmed/failed) ----
  // ★MERGE-GATE(Phase D codexloop F1): 세 검증 phase 모두 명시 분기 필수.
  // 누락 시 말미 generic 에러 화면으로 폴스루 → Phase D happy-path가 에러로 보임.
  if (phase === 'verifying' || phase === 'verify_confirmed' || phase === 'verify_failed') {
    return (
      <OnboardingVerifyScreens
        phase={phase}
        verifyRemainingSec={verifyRemainingSec}
        onRetry={complete}
        onClose={onClose}
        bankCode={session?.bank_code ?? ''}
      />
    );
  }

  // ---- 성공(provisional) ----
  if (phase === 'completed') {
    return (
      <StatusScreen
        tone="teal"
        icon={<Check className="h-7 w-7" />}
        title="계좌 연동을 시작했어요"
        footer={
          <Button className="h-11 w-full rounded-xl bg-teal-600 font-semibold text-white shadow-sm transition-colors hover:bg-teal-700" onClick={onClose}>
            확인
          </Button>
        }
      >
        <p className="mt-2 max-w-[19rem] text-sm leading-relaxed text-slate-500">
          첫 손님 입금이 이 계좌로 들어오면 자동으로 확인돼요. 그때까지는{' '}
          <span className="font-medium text-slate-700">연동됨(확인 대기)</span> 상태예요.
        </p>
        {registered && (
          <span className="mt-3 inline-flex items-center gap-1 rounded-full bg-teal-50 px-2.5 py-1 text-xs font-medium text-teal-700 ring-1 ring-teal-100">
            <ShieldCheck className="h-3.5 w-3.5" /> 등록 확인됨
          </span>
        )}
      </StatusScreen>
    );
  }

  // ---- 게이트웨이 점유(친절 거절) ----
  if (phase === 'gateway_busy') {
    const mins = busyUntil ? Math.max(1, Math.ceil((busyUntil.getTime() - Date.now()) / 60000)) : null;
    return (
      <StatusScreen
        tone="amber"
        icon={<Clock className="h-7 w-7" />}
        title="다른 사장님이 연동 중이에요"
        footer={
          <Button variant="outline" className="h-11 w-full rounded-xl border-slate-200 font-medium text-slate-600 hover:bg-slate-50" onClick={reset}>
            다시 시도
          </Button>
        }
      >
        <p className="mt-2 text-sm leading-relaxed text-slate-500">
          {mins ? `약 ${mins}분 후` : '잠시 후'}에 다시 시도해주세요.
        </p>
      </StatusScreen>
    );
  }

  // ---- 코드 만료(트러블슈팅) ----
  if (phase === 'code_expired') {
    let troubleshootingItems: { text: string; isCritical?: boolean }[];
    if (session?.bank_code === 'Shinhan') {
      troubleshootingItems = [
        { text: '신한 SOL뱅크에서 입출금 문자 신청이 완료됐는지 확인해주세요.' },
        { text: `추가등록할 통지휴대폰 번호를 ${GATEWAY_NUMBER_DISPLAY}로 정확히 입력했는지 확인해주세요.` },
        { text: '통지항목에 “입금거래” 또는 “모든거래”를 선택했는지 확인해주세요.' },
        {
          text: '계좌번호표시여부를 “표시함”으로 설정했는지 확인해주세요.',
          isCritical: true,
        },
      ];
    } else if (session?.bank_code === 'Nonghyup') {
      troubleshootingItems = [
        { text: '농협은행 앱에서 문자알림신청이 완료됐는지 확인해주세요.' },
        { text: `알림받을 휴대전화 번호를 ${GATEWAY_NUMBER_DISPLAY}로 정확히 입력했는지 확인해주세요.` },
        {
          text: '금액제한을 “제한없음”으로 설정했는지 확인해주세요.',
          isCritical: true,
        },
      ];
    } else if (session?.bank_code === 'IBK') {
      troubleshootingItems = [
        { text: '기업은행 앱에서 입출금 알림(문자) 신청이 완료됐는지 확인해주세요.' },
        { text: `알림 휴대폰번호를 ${GATEWAY_NUMBER_DISPLAY}로 정확히 입력했는지 확인해주세요.` },
        {
          text: '계좌번호표시구분을 “일반계좌번호”로 설정했는지 확인해주세요.',
          isCritical: true,
        },
      ];
    } else {
      troubleshootingItems = [
        { text: '우리은행 모바일 앱에서 SMS 입출금통지를 신청했는지 확인해주세요.' },
        { text: `수신번호를 ${GATEWAY_NUMBER_DISPLAY}로 정확히 입력했는지 확인해주세요.` },
        { text: '통지 옵션(알림 금액 “모두”)을 맞췄는지 확인해주세요.' },
      ];
    }

    return (
      <StatusScreen
        tone="amber"
        icon={<AlertTriangle className="h-7 w-7" />}
        title="인증번호가 만료됐어요"
        footer={
          <Button className="h-11 w-full rounded-xl bg-teal-600 font-semibold text-white shadow-sm transition-colors hover:bg-teal-700" onClick={cancel}>
            처음부터 다시
          </Button>
        }
      >
        <p className="mt-2 text-sm leading-relaxed text-slate-500">3분이 지나 인증번호가 만료됐어요. 처음부터 다시 시도해주세요.</p>
        <ul className="mt-4 w-full space-y-2 rounded-xl bg-slate-50 p-4 text-left text-xs text-slate-500 ring-1 ring-slate-100">
          {troubleshootingItems.map((item) => (
            <li
              key={item.text}
              className={cn(
                'flex gap-2',
                item.isCritical && 'font-semibold text-rose-600',
              )}
            >
              <span className={item.isCritical ? 'text-rose-300' : 'text-slate-300'}>•</span>
              {item.text}
            </li>
          ))}
        </ul>
      </StatusScreen>
    );
  }

  // ---- 에러 ----
  return (
    <StatusScreen
      tone="rose"
      icon={<AlertTriangle className="h-7 w-7" />}
      title="문제가 발생했어요"
      footer={
        <Button variant="outline" className="h-11 w-full rounded-xl border-slate-200 font-medium text-slate-600 hover:bg-slate-50" onClick={reset}>
          다시 시도
        </Button>
      }
    >
      <p className="mt-2 text-sm leading-relaxed text-slate-500">{errorMessage ?? '잠시 후 다시 시도해주세요.'}</p>
    </StatusScreen>
  );
}
